import { useState, type SVGProps } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '@/hooks/use-auth'
import { useChurch } from '@/hooks/useChurch'
import { useChurchColors } from '@/hooks/useChurchColors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ThemeSelector } from '@/components/ui/theme-selector'
import { Loader2, Mail, Lock, Eye, EyeOff, Sparkles } from 'lucide-react'

const loginSchema = z.object({
  email: z.string().email('E-mail inválido'),
  password: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
})

type LoginForm = z.infer<typeof loginSchema>

const GoogleIcon = ({ className, ...props }: SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className={className}
    {...props}
  >
    <path
      d="M21.35 11.1H12v3.8h5.3c-.23 1.2-.93 2.3-1.98 3.02l3.19 2.47c1.86-1.72 2.94-4.26 2.94-7.29 0-.73-.07-1.43-.19-2.1Z"
      fill="#4285F4"
    />
    <path
      d="M12 22c2.7 0 4.97-.9 6.63-2.42l-3.19-2.47c-.88.6-2 .96-3.44.96-2.65 0-4.9-1.79-5.7-4.19l-3.29 2.53C4.77 19.9 8.1 22 12 22Z"
      fill="#34A853"
    />
    <path
      d="M6.3 13.88c-.2-.6-.32-1.25-.32-1.88s.12-1.28.32-1.88L2.95 7.59A9.97 9.97 0 0 0 2 12a9.97 9.97 0 0 0 .95 4.41l3.35-2.53Z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.5c1.47 0 2.79.51 3.84 1.5l2.87-2.85C17 2.88 14.7 2 12 2 8.1 2 4.77 4.1 3.05 7.59l3.29 2.53C7.1 7.29 9.35 5.5 12 5.5Z"
      fill="#EA4335"
    />
  </svg>
)

// Apple social login removed per request

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, loginWithProvider, isLoggingWithProvider } = useAuth()
  const { church, loading: churchLoading, currentLogo, hasLogo } = useChurch()
  const { primaryColor, secondaryColor } = useChurchColors()
  const [error, setError] = useState<string | null>(null)
  const registrationState = (location.state as { fromRegister?: boolean; registeredEmail?: string } | null) || null
  const shouldShowRegisterSuccess = registrationState?.fromRegister
  const [socialLoading, setSocialLoading] = useState<'google' | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginForm) => {
    try {
      setError(null)
      await login(data)
      navigate('/')
    } catch {
      setError('E-mail ou senha incorretos. Tente novamente.')
    }
  }

  const handleSocialLogin = async (provider: 'google') => {
    try {
      setError(null)
      setSocialLoading(provider)
      await loginWithProvider(provider)
    } catch {
      setError('Não foi possível entrar com o provedor selecionado.')
    } finally {
      setSocialLoading(null)
    }
  }
  

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="relative"
    >
      {/* Seletor de tema no canto superior direito */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeSelector />
      </div>
      
      <motion.div
        className="absolute inset-4 -z-10 rounded-3xl blur-2xl"
        style={{
          background: `linear-gradient(to bottom right, ${primaryColor}10, ${secondaryColor}10, hsl(var(--background))40)`
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1, duration: 0.6 }}
      />
      <Card 
        className="overflow-hidden shadow-2xl backdrop-blur"
        style={{
          borderColor: `${primaryColor}20`,
          boxShadow: `0 25px 50px -12px ${primaryColor}20`
        }}
      >
        <div 
          className="h-2 w-full"
          style={{
            background: `linear-gradient(to right, ${primaryColor}, ${primaryColor}70, ${secondaryColor})`
          }}
        />
        <CardHeader className="space-y-4 text-center">
          {/* Logo da Igreja - Maior e sem fundo */}
          {hasLogo && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="mx-auto"
            >
              <img 
                src={currentLogo!} 
                alt={`Logo ${church?.nome || 'Igreja'}`}
                className="h-24 w-24 object-contain"
                onError={(e) => {
                  // Esconde a imagem se não carregar
                  e.currentTarget.style.display = 'none'
                }}
              />
            </motion.div>
          )}
          
          {/* Ícone padrão quando não há logo */}
          {!hasLogo && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary/70 shadow-lg shadow-primary/20"
            >
              <Sparkles className="h-8 w-8 text-white" />
            </motion.div>
          )}
          
          <CardTitle className="text-2xl">
            {churchLoading ? (
              <div className="flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Carregando...
              </div>
            ) : (
              church?.nome ? `Bem-vindo à ${church.nome}!` : 'Bem-vindo de volta!'
            )}
          </CardTitle>
          <CardDescription>
            {churchLoading ? (
              'Carregando informações...'
            ) : (
              church?.descricao || 'Entre com suas credenciais para acessar o sistema'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="pb-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                className="w-full shadow-sm"
                style={{
                  borderColor: primaryColor,
                  '--ring': primaryColor
                } as React.CSSProperties}
                onClick={() => handleSocialLogin('google')}
                disabled={isSubmitting || isLoggingWithProvider || socialLoading !== null}
              >
                {socialLoading === 'google' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Conectando com Google...
                  </>
                ) : (
                  <>
                    <GoogleIcon className="mr-2 h-4 w-4" />
                    Entrar com Google
                  </>
                )}
              </Button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-dashed border-muted" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-3 text-muted-foreground">ou continue com e-mail</span>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="email" className="text-sm font-medium">
                E-mail
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  {...register('email')}
                  disabled={isSubmitting || socialLoading !== null}
                  className="pl-10"
                  style={{
                    '--ring': primaryColor
                  } as React.CSSProperties}
                />
              </div>
              {errors.email && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-destructive"
                >
                  {errors.email.message}
                </motion.p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="password" className="text-sm font-medium">
                Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  {...register('password')}
                  disabled={isSubmitting || socialLoading !== null}
                  className="pl-10 pr-10"
                  style={{
                    '--ring': primaryColor
                  } as React.CSSProperties}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-destructive"
                >
                  {errors.password.message}
                </motion.p>
              )}
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-lg bg-destructive/10 p-4 ring-1 ring-destructive/20"
              >
                <p className="text-sm text-destructive">{error}</p>
              </motion.div>
            )}

            {shouldShowRegisterSuccess && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-lg bg-emerald-50 p-4 ring-1 ring-emerald-200"
              >
                <p className="text-sm text-emerald-700">
                  Cadastro concluído! Entre com{' '}
                  <span className="font-semibold">{registrationState?.registeredEmail}</span> para
                  acessar o sistema.
                </p>
              </motion.div>
            )}

            <Button
              type="submit"
              className="w-full shadow-md"
              style={{
                backgroundColor: primaryColor,
                borderColor: primaryColor,
                boxShadow: `0 4px 14px 0 ${primaryColor}20`,
                color: 'white'
              }}
              size="lg"
              disabled={isSubmitting || socialLoading !== null}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Não tem uma conta?{' '}
              <Link 
                to="/register" 
                className="font-medium hover:underline"
                style={{ color: primaryColor }}
              >
                Cadastre-se agora
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </motion.div>
  )
}
